import { useCallback, useEffect } from 'react';
import { Button, Col, Form, Modal, Row, Stack } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import {
  CustomUserProfileFieldEnum,
  UserPermissionEnum,
} from 'src/models/GalenData/CustomField.model';
import { CustomFieldData } from 'src/models/GalenData/CustomFieldData.model';
import { convertUserPermissionToObj } from 'src/models/GalenData/helpers/saveSettings';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import {
  emailValidators,
  firstNameValidators,
  lastNameValidators,
} from 'src/utils/formValidators';
import { generateSecurePassword } from 'src/utils/generateSecurePassword';

export type EditUserFormDataTypes = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  create: boolean;
  list: boolean;
  read: boolean;
  print: boolean;
  update: boolean;
  hasMfa: boolean;
};

interface IEditUserFormModalProps {
  show: boolean;
  handleClose: () => void;
  user: GalenDataUser;
  pageSize: number;
  currentPage: number;
  searchValues: { emailAddress: string; name: string };
  handleGetUsers: (
    pageSize?: number,
    pageNumber?: number,
    searchValues?: { emailAddress: string; name: string },
  ) => Promise<PageOfUser>;
  userSettingsData: CustomFieldData<CustomUserProfileFieldEnum>[];
}

const EditUserFormModal = ({
  show,
  handleClose,
  user,
  pageSize,
  currentPage,
  searchValues,
  handleGetUsers,
  userSettingsData,
}: IEditUserFormModalProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { userEmail } = useGetCurrentUser();

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors, isDirty, isSubmitting },
    watch,
    setError,
    clearErrors,
  } = useForm<EditUserFormDataTypes>({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      create: true,
      list: false,
      read: false,
      print: false,
      update: false,
    },
    criteriaMode: 'all',
    mode: 'onTouched',
  });

  const create = watch('create');
  const list = watch('list');

  useEffect(() => {
    if (!create && !list) {
      setError('root', {
        type: 'manual',
        message: t('message.error.eitherCreateOrListPermission', {
          ns: I18NextNS.COMMON,
        }),
      });
    } else {
      clearErrors('root');
    }
  }, [create, list, setError, clearErrors, t]);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  useEffect(() => {
    const userPermissions: UserPermissionEnum[] = userSettingsData.find(
      (setting) =>
        setting.field.name === CustomUserProfileFieldEnum.UserPermission,
    )?.fieldData;

    const userPermissionObj = convertUserPermissionToObj(userPermissions);

    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.emailAddress,
      hasMfa: user.mfaEnable,
      create: userPermissionObj.create,
      list: userPermissionObj.list,
      read: userPermissionObj.read,
      print: userPermissionObj.print,
      update: userPermissionObj.update,
    });
  }, [user, reset, userSettingsData]);

  const handleCloseAndReset = () => {
    handleClose();
    reset();
  };

  const handleResetPassword = useCallback(
    (user: GalenDataUser) => {
      Api.confirmBox({
        title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
        message: t('users.resetPasswordMessage', {
          emailAddress: user.emailAddress,
          fullName: user.fullName,
        }),
        locale: language,
        callback: async (yes) => {
          if (!yes) {
            return;
          }

          try {
            if (!user.emailAddress) {
              throw new Error(
                t('message.error.userEmailNotSet', { ns: I18NextNS.COMMON }),
              );
            }

            const newPassword = generateSecurePassword();
            await Api.remote.change_password(
              user.userId,
              '',
              newPassword,
              true,
            );

            Api.alertBox({
              title: t('bootbox.title.Success', { ns: I18NextNS.COMMON }),
              message: t('users.resetPasswordSuccessMessage', {
                emailAddress: user.emailAddress,
                fullName: user.fullName,
                newPassword,
              }),
              locale: language,
              type: 'success',
            });
          } catch (error) {
            const errorMessage = handleHttpRequestError(error);
            Api.alertBox({
              title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
              message: errorMessage,
              locale: language,
              type: 'error',
            });
          }
        },
      });
    },
    [handleHttpRequestError, language, t],
  );

  const handleEditUser = async (data: EditUserFormDataTypes) => {
    try {
      if (!user) {
        throw new Error(
          t('message.error.userNotSet', { ns: I18NextNS.COMMON }),
        );
      }

      const { firstName, lastName, emailAddress } = data;

      const updateUserPermissions = (
        Object.keys(data) as Array<keyof typeof data>
      ).filter((key) => {
        return (
          data[key] &&
          Object.values(UserPermissionEnum).includes(key as UserPermissionEnum)
        );
      });

      const updatedUser = {
        ...user,
        ...data,
      };

      await Promise.all([
        Api.remote.saveUserSettings({
          userSettingsData: userSettingsData ?? [],
          data: {
            [CustomUserProfileFieldEnum.UserPermission]: updateUserPermissions,
          },
          user: updatedUser,
        }),
        Api.remote.update_user(updatedUser),
      ]);

      // TODO: Not in promise.all due to race condition bug in Galen (GCS-582)
      await Api.remote.set_user_mfa(data.hasMfa, user.userId);

      await handleGetUsers(pageSize, currentPage - 1, searchValues);

      Api.alertBox({
        title: t('bootbox.title.Success', { ns: I18NextNS.COMMON }),
        message: t('users.updateUserSuccessMessage', {
          emailAddress,
          fullName: `${firstName} ${lastName}`,
        }),
        locale: language,
        type: 'success',
      });

      handleCloseAndReset();
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    }
  };

  return (
    <CustomModal show={show} onHide={handleCloseAndReset} size="lg">
      <Form onSubmit={handleSubmit(handleEditUser)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('users.edit')} {t('users.user')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col className="border-end" md={8}>
              <h5 className="mb-3">
                {t('users.user')} {t('users.information')}
              </h5>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('patientFields.First Name', {
                    ns: I18NextNS.COMMON,
                  })}
                </Form.Label>

                <Form.Control
                  type="text"
                  placeholder={t('patientFields.First Name', {
                    ns: I18NextNS.COMMON,
                  })}
                  data-test="first-name-input"
                  {...register('firstName', firstNameValidators(t))}
                />
                {errors.firstName?.message && (
                  <p className="text-danger">{errors.firstName.message}</p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {t('patientFields.Last Name', { ns: I18NextNS.COMMON })}
                </Form.Label>
                <Form.Control
                  placeholder={t('patientFields.Last Name', {
                    ns: I18NextNS.COMMON,
                  })}
                  data-test="last-name-input"
                  {...register('lastName', lastNameValidators(t))}
                />
                {errors.lastName?.message && (
                  <p className="text-danger ">{errors.lastName.message}</p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {t('patientFields.email', { ns: I18NextNS.COMMON })}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('patientFields.emailPlaceholder', {
                    ns: I18NextNS.COMMON,
                  })}
                  data-test="email-input"
                  autoComplete="off"
                  {...register('emailAddress', emailValidators(t))}
                ></Form.Control>
                {errors.emailAddress?.message && (
                  <p className="text-danger ">{errors.emailAddress.message}</p>
                )}
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    variant="link"
                    className="text-primary p-0"
                    data-test="reset-password-button"
                    onClick={() => {
                      handleResetPassword(user);
                    }}
                    disabled={user.emailAddress === userEmail}
                  >
                    {t('users.resetPassword')}
                  </Button>
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <h5 className="mb-3">
                {t('users.user')} {t('users.permissions')}
              </h5>
              <Form.Group>
                <Stack gap={2}>
                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.Create Sessions')}
                    {...register('create')}
                    className="custom-switch"
                    data-test="create-session-permission"
                  />

                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.List Sessions')}
                    {...register('list')}
                    className="custom-switch"
                    data-test="list-session-permission"
                  />

                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.View Reports')}
                    {...register('read')}
                    className="custom-switch"
                    data-test="view-report-permission"
                  />

                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.Print/Download Reports')}
                    {...register('print')}
                    className="custom-switch"
                    data-test="print-report-permission"
                  />

                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.editReports')}
                    {...register('update')}
                    className="custom-switch"
                    data-test="edit-report-permission"
                  />

                  {errors.root && (
                    <p className="text-danger">{errors.root.message}</p>
                  )}
                </Stack>

                <Stack className="mt-3">
                  <h5 className="mb-3">
                    {t('users.user')} {t('users.MFA')}
                  </h5>
                  <Form.Check
                    type="switch"
                    label={t('users.permissionFields.MFA')}
                    {...register('hasMfa')}
                    className="custom-switch"
                  />
                </Stack>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="primary-100" onClick={handleCloseAndReset}>
            {t('users.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            data-test="submit-member-form"
            disabled={
              isSubmitting || !isDirty || Object.values(errors).length > 0
            }
          >
            {isSubmitting ? t('users.saving') : t('users.save')}
          </Button>
        </Modal.Footer>
      </Form>
    </CustomModal>
  );
};

export default EditUserFormModal;
